import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { AdminNavigation } from './AdminNavigation';
import './admin-dashboard.scss';

interface SOPLog {
  id: string;
  technicianId: string;
  technicianEmail: string;
  sopName: string;
  startTime: Date;
  endTime?: Date;
  steps: Array<{
    name: string;
    status: string;
    timestamp: Date;
    evidence: string;
    message?: string;
  }>;
  videoUrl?: string;
  videoSession?: {
    sessionId: string;
    startTime: Date;
    endTime?: Date;
    totalDuration: number;
    status: string;
    storageUrls: string[];
    chunkCount: number;
  };
  videoChunks?: Array<{
    chunkIndex: number;
    url: string;
    timestamp: Date;
    size: number;
  }>;
  status: 'in-progress' | 'completed' | 'abandoned';
}

export const AdminDashboard: React.FC = () => {
  const [sopLogs, setSopLogs] = useState<SOPLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // Fetch SOP logs
        const sopQuery = query(collection(db, 'sopLogs'), orderBy('startTime', 'desc'));
        const sopSnapshot = await getDocs(sopQuery);
        const sopLogs: SOPLog[] = [];
        
        sopSnapshot.forEach((doc) => {
          const data = doc.data();
          sopLogs.push({
            id: doc.id,
            ...data,
            startTime: data.startTime.toDate(),
            endTime: data.endTime?.toDate(),
            videoSession: data.videoSession ? {
              ...data.videoSession,
              startTime: data.videoSession.startTime.toDate(),
              endTime: data.videoSession.endTime?.toDate()
            } : undefined,
            videoChunks: data.videoChunks?.map((chunk: any) => ({
              ...chunk,
              timestamp: chunk.timestamp.toDate()
            })),
            steps: data.steps.map((step: any) => ({
              ...step,
              timestamp: step.timestamp.toDate()
            }))
          } as SOPLog);
        });
        
        setSopLogs(sopLogs);
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);


  const formatDuration = (start: Date, end?: Date) => {
    if (!end) return 'In Progress';
    const diff = end.getTime() - start.getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'in-progress': return '#ff9800';
      case 'abandoned': return '#f44336';
      default: return '#999';
    }
  };


  const deleteStorageFolderRecursively = async (folderPath: string) => {
    try {
      const folderRef = ref(storage, folderPath);
      const listResult = await listAll(folderRef);
      // Delete all files in this folder
      await Promise.all(listResult.items.map((itemRef) => deleteObject(itemRef)));
      // Recurse into subfolders
      await Promise.all(listResult.prefixes.map((prefixRef) => deleteStorageFolderRecursively(prefixRef.fullPath)));
    } catch (err) {
      console.warn('Storage folder delete warning:', folderPath, err);
    }
  };

  const handleDeleteSOPLog = async (log: SOPLog) => {
    const confirmMsg = `Delete SOP log for ${log.sopName} by ${log.technicianEmail}? This will also delete any associated videos.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      // Delete Firestore document
      await deleteDoc(doc(db, 'sopLogs', log.id));

      // Attempt to delete structured session folder if exists
      await deleteStorageFolderRecursively(`sop-videos/${log.id}`);

      // Attempt to delete single-file video if present (legacy path)
      try {
        await deleteObject(ref(storage, `sop-videos/${log.id}.webm`));
      } catch (_) {
        // ignore if not present
      }

      // Optimistically update UI
      setSopLogs((prev) => prev.filter((l) => l.id !== log.id));
    } catch (error) {
      console.error('Failed to delete SOP log:', error);
      alert('Failed to delete SOP log. See console for details.');
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading SOP logs...</div>;
  }

  return (
    <div className="admin-dashboard">
      <AdminNavigation />
      <header className="admin-header">
        <h1>Admin Dashboard - SOP Logs</h1>
      </header>

      {/* Reports moved to /admin/reports */}
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total SOPs</h3>
          <p>{sopLogs.length}</p>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <p>{sopLogs.filter(log => log.status === 'completed').length}</p>
        </div>
        <div className="stat-card">
          <h3>In Progress</h3>
          <p>{sopLogs.filter(log => log.status === 'in-progress').length}</p>
        </div>
        <div className="stat-card">
          <h3>Abandoned</h3>
          <p>{sopLogs.filter(log => log.status === 'abandoned').length}</p>
        </div>
      </div>

      <div className="logs-container">
        <h2>Recent SOP Performances</h2>
        <div className="logs-table">
          <table>
            <thead>
              <tr>
                <th>Technician</th>
                <th>SOP</th>
                <th>Start Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Video</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sopLogs.map((log) => (
                <tr 
                  key={log.id}
                  className="clickable-row"
                  onClick={() => navigate(`/admin/sop/${log.id}`)}
                >
                  <td>{log.technicianEmail}</td>
                  <td>{log.sopName}</td>
                  <td>{log.startTime.toLocaleString()}</td>
                  <td>{formatDuration(log.startTime, log.endTime)}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(log.status) }}
                    >
                      {log.status}
                    </span>
                  </td>
                  <td>
                    {log.videoSession ? (
                      <div>
                        <span style={{ color: '#4caf50', fontSize: '0.8rem' }}>✓ Recorded</span>
                        <br />
                        <span style={{ fontSize: '0.7rem', color: '#666' }}>
                          {log.videoSession.chunkCount} chunks
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>No video</span>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="icon-btn delete-icon-btn"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSOPLog(log); }}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M9 3h6a1 1 0 0 1 1 1v2h5v2h-2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8H3V6h5V4a1 1 0 0 1 1-1zm1 3h4V5h-4v1zm-1 5v8h2v-8H9zm4 0v8h2v-8h-2z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}; 